import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminInquiryListItem } from '../../api/admin.api';

interface InquiryDetailModalProps {
  inquiry: AdminInquiryListItem;
  onClose: () => void;
}

export const InquiryDetailModal: React.FC<InquiryDetailModalProps> = ({
  inquiry,
  onClose,
}) => {
  const [replyContent, setReplyContent] = useState('');
  const queryClient = useQueryClient();

  const replyMutation = useMutation({
    mutationFn: (content: string) => adminApi.createInquiryReply(inquiry.id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminInquiries'] });
      setReplyContent('');
      alert('답변이 등록되었습니다.');
      onClose();
    },
    onError: () => {
      alert('답변 등록에 실패했습니다.');
    },
  });

  const getStatusBadgeStyles = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-[#FEF3C7] text-[#92400E]';
      case 'IN_PROGRESS':
        return 'bg-[#DBEAFE] text-[#1E40AF]';
      case 'COMPLETED':
        return 'bg-[#D1FAE5] text-[#065F46]';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '대기중';
      case 'IN_PROGRESS':
        return '진행중';
      case 'COMPLETED':
        return '완료';
      default:
        return status;
    }
  };

  const handleSubmitReply = () => {
    if (replyContent.trim()) {
      replyMutation.mutate(replyContent.trim());
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 pointer-events-none">
        {/* Desktop/Tablet Modal */}
        <div
          className="hidden md:block bg-white rounded-xl shadow-[0_20px_25px_rgba(0,0,0,0.15)] w-full max-w-[600px] max-h-[90vh] overflow-hidden pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#030213]">문의 상세</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg
                className="w-6 h-6 text-[#6B7280]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)] px-6 py-4">
            {/* Info Section - 2x2 Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-sm text-[#6B7280] mb-1">타입</div>
                <div className="text-sm text-[#030213] font-medium">
                  {inquiry.inquiryType}
                </div>
              </div>
              <div>
                <div className="text-sm text-[#6B7280] mb-1">작성자</div>
                <div className="text-sm text-[#030213] font-medium">
                  {inquiry.authorNickname}{' '}
                  <span className="text-[#6B7280]">({inquiry.authorEmail})</span>
                </div>
              </div>
              <div>
                <div className="text-sm text-[#6B7280] mb-1">작성일</div>
                <div className="text-sm text-[#030213] font-medium">
                  {new Date(inquiry.regDateTime).toLocaleString('ko-KR')}
                </div>
              </div>
              <div>
                <div className="text-sm text-[#6B7280] mb-1">상태</div>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeStyles(inquiry.status)}`}
                >
                  {getStatusLabel(inquiry.status)}
                </span>
              </div>
            </div>

            {/* Content Section */}
            <div className="mb-6">
              <div className="text-sm text-[#6B7280] mb-2">문의 내용</div>
              <div className="text-sm text-[#030213] leading-relaxed bg-[#f3f3f5] rounded-lg p-4">
                {inquiry.content}
              </div>
            </div>

            {/* Replies Section */}
            <div className="mb-6">
              <div className="text-sm text-[#6B7280] mb-3">
                답변 ({inquiry.replyCount}개)
              </div>
              {inquiry.replyCount > 0 ? (
                <div className="text-sm text-[#6B7280] text-center py-4">
                  답변 내역은 개별 조회를 통해 확인할 수 있습니다.
                </div>
              ) : (
                <div className="text-sm text-[#6B7280] text-center py-4">
                  아직 답변이 없습니다.
                </div>
              )}
            </div>
          </div>

          {/* Reply Form - Fixed Bottom */}
          <div className="border-t border-[#E5E7EB] px-6 py-4 bg-white">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="답변 내용을 입력하세요"
              className="w-full bg-[#f3f3f5] rounded-lg p-3 text-sm text-[#030213] placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#030213] resize-none"
              rows={3}
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-[#6B7280] bg-white border border-[#E5E7EB] rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSubmitReply}
                disabled={!replyContent.trim() || replyMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-[#030213] rounded-lg hover:bg-[#030213]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {replyMutation.isPending ? '등록 중...' : '답변 등록하기'}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Bottom Sheet */}
        <div
          className="md:hidden fixed inset-0 bg-white rounded-t-3xl overflow-hidden pointer-events-auto animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Handle Bar */}
          <div className="flex justify-center py-3 border-b border-[#E5E7EB]">
            <div className="w-12 h-1 bg-[#E5E7EB] rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#030213]">문의 상세</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg
                className="w-6 h-6 text-[#6B7280]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div
            className="overflow-y-auto px-4 py-4"
            style={{ height: 'calc(100vh - 220px)' }}
          >
            {/* Info Section - Stacked on Mobile */}
            <div className="space-y-3 mb-6">
              <div>
                <div className="text-xs text-[#6B7280] mb-1">타입</div>
                <div className="text-sm text-[#030213] font-medium">
                  {inquiry.inquiryType}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#6B7280] mb-1">작성자</div>
                <div className="text-sm text-[#030213] font-medium">
                  {inquiry.authorNickname}
                </div>
                <div className="text-xs text-[#6B7280]">{inquiry.authorEmail}</div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-[#6B7280] mb-1">작성일</div>
                  <div className="text-sm text-[#030213] font-medium">
                    {new Date(inquiry.regDateTime).toLocaleDateString('ko-KR')}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeStyles(inquiry.status)}`}
                >
                  {getStatusLabel(inquiry.status)}
                </span>
              </div>
            </div>

            {/* Content Section */}
            <div className="mb-6">
              <div className="text-sm text-[#6B7280] mb-2">문의 내용</div>
              <div className="text-sm text-[#030213] leading-relaxed bg-[#f3f3f5] rounded-lg p-3">
                {inquiry.content}
              </div>
            </div>

            {/* Replies Section */}
            <div className="mb-6">
              <div className="text-sm text-[#6B7280] mb-3">
                답변 ({inquiry.replyCount}개)
              </div>
              {inquiry.replyCount > 0 ? (
                <div className="text-sm text-[#6B7280] text-center py-4">
                  답변 내역은 개별 조회를 통해 확인할 수 있습니다.
                </div>
              ) : (
                <div className="text-sm text-[#6B7280] text-center py-4">
                  아직 답변이 없습니다.
                </div>
              )}
            </div>
          </div>

          {/* Reply Form - Fixed Bottom */}
          <div className="fixed bottom-0 left-0 right-0 border-t border-[#E5E7EB] px-4 py-3 bg-white">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="답변 내용을 입력하세요"
              className="w-full bg-[#f3f3f5] rounded-lg p-3 text-sm text-[#030213] placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#030213] resize-none mb-2"
              rows={2}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm text-[#6B7280] bg-white border border-[#E5E7EB] rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSubmitReply}
                disabled={!replyContent.trim() || replyMutation.isPending}
                className="flex-1 px-4 py-2 text-sm text-white bg-[#030213] rounded-lg hover:bg-[#030213]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {replyMutation.isPending ? '등록 중...' : '답변 등록하기'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};
